/**
 * Композабл для отслеживания **фактически полезной** высоты viewport в пикселях.
 *
 * **Источник:** при наличии Visual Viewport API — `visualViewport.height` (мобильные панели
 * браузера); иначе — `document.documentElement.clientHeight`.
 *
 * **Почему не только `resize`:** приложение может быть во **вложенном iframe** — родитель
 * меняет высоту или «выезжает» шторка без `window.resize` внутри фрейма. Тогда срабатывают
 * `ResizeObserver` на корне документа и события `visualViewport` (`resize` / `scroll`).
 *
 * **Паттерн:** на время показа полноэкранного UI — `measure()` и {@link subscribeResize};
 * при скрытии — {@link unsubscribeResize}. Либо {@link Options.subscribeResizeOnMount}.
 *
 * На SSR `measure` ничего не делает; `clientHeightPx` остаётся `0` до клиента.
 */
import throttle from 'lodash/throttle.js'
import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

/** Параметры {@link useDocumentClientHeight}. */
type Options = {
  /**
   * Задержка throttle для внутренних колбэков (мс).
   * @defaultValue 50
   */
  throttleMs?: number
  /**
   * Вызывается только внутри throttled-колбэка перед `measure`.
   * Если вернёт `false`, высота не пересчитывается (например, пока нет `ref` на контейнер модалки).
   * На прямой вызов `measure()` не влияет.
   */
  beforeResizeMeasure?: () => boolean
  /**
   * В `onMounted`: первый `measure`, {@link subscribeResize};
   * в `onBeforeUnmount`: {@link unsubscribeResize}.
   */
  subscribeResizeOnMount?: boolean
}

function readViewportHeightPx(): number {
  const win = globalThis.window
  if (!win) return 0

  const vv = win.visualViewport
  const fromVv = vv?.height
  const fromDoc = win.document.documentElement.clientHeight

  return Math.round(fromVv ? fromVv : fromDoc)
}

/**
 * @param options — см. {@link Options}
 * @returns Поля см. описание в модульном комментарии выше.
 */
export function useDocumentClientHeight(options?: Options): {
  clientHeightPx: Ref<number>
  measure: () => void
  onResizeThrottled: ReturnType<typeof throttle>
  subscribeResize: () => void
  unsubscribeResize: () => void
} {
  const throttleMs = options?.throttleMs ?? 50
  const beforeResizeMeasure = options?.beforeResizeMeasure
  const subscribeResizeOnMount = options?.subscribeResizeOnMount ?? false
  const clientHeightPx = ref(0)

  const measure = () => {
    clientHeightPx.value = readViewportHeightPx()
  }

  const onResizeThrottled = throttle(() => {
    if (beforeResizeMeasure && !beforeResizeMeasure()) return
    measure()
  }, throttleMs)

  let subscriptionActive = false
  let rootResizeObserver: ResizeObserver | null = null

  const subscribeResize = () => {
    const win = globalThis.window
    if (!win || subscriptionActive) return

    subscriptionActive = true

    win.addEventListener('resize', onResizeThrottled)
    win.visualViewport?.addEventListener('resize', onResizeThrottled)
    win.visualViewport?.addEventListener('scroll', onResizeThrottled)

    if (ResizeObserver) {
      rootResizeObserver = new ResizeObserver(() => {
        onResizeThrottled()
      })
      rootResizeObserver.observe(win.document.documentElement)
      if (win.document.body) {
        rootResizeObserver.observe(win.document.body)
      }
    }
  }

  const unsubscribeResize = () => {
    const win = globalThis.window
    if (win == null || !subscriptionActive) return

    subscriptionActive = false

    win.removeEventListener('resize', onResizeThrottled)
    win.visualViewport?.removeEventListener('resize', onResizeThrottled)
    win.visualViewport?.removeEventListener('scroll', onResizeThrottled)

    rootResizeObserver?.disconnect()
    rootResizeObserver = null
    onResizeThrottled.cancel()
  }

  if (subscribeResizeOnMount) {
    onMounted(() => {
      measure()
      subscribeResize()
    })
    onBeforeUnmount(() => {
      unsubscribeResize()
    })
  }

  return {
    clientHeightPx,
    measure,
    onResizeThrottled,
    subscribeResize,
    unsubscribeResize
  }
}
